import React, { useState, useEffect, useRef, useCallback } from 'react';
import Icon from './icons';
import { tx } from './i18n';

// ── Paleta polecen (Ctrl/Cmd+K) ──────────────────────────────────────────────
// Aplikacja ma ponad 50 widokow w kilku grupach menu -- dotarcie do rzadziej
// uzywanego wymaga rozwiniecia grupy i szukania wzrokiem. Paleta pozwala
// wpisac fragment nazwy i wejsc tam Enterem.
//
// Gdy wpisany tekst wyglada jak znak wywolawczy, na gorze listy pojawiaja
// sie akcje: karta stacji i szukanie tego znaku w dzienniku.
//
// Rozwiazuje TEZ drugi problem: skroty klawiszowe (Ctrl+Enter zapisuje QSO,
// Esc czysci formularz) istnialy, ale NIKT o nich nie wiedzial -- sa wypisane
// na dole palety.

// Dopasowanie "rozmyte": wpisane litery musza wystapic w nazwie PO KOLEI,
// ale niekoniecznie obok siebie. Dzieki temu "map dx" znajdzie "Mapa DXCC".
function fuzzyMatch(needle, haystack) {
  const n = needle.toLowerCase().replace(/\s+/g, '');
  const h = haystack.toLowerCase();
  if (!n) return true;
  let i = 0;
  for (const ch of h) {
    if (ch === n[i]) i++;
    if (i === n.length) return true;
  }
  return false;
}

const stripEmoji = (label) => (label || '').replace(/^\S+\s+/, '');
const CALL_RE = /^([A-Z0-9]{1,4}\/)?[A-Z0-9]{1,3}[0-9][A-Z0-9]{0,5}(\/[A-Z0-9]{1,4})?$/;

export default function CommandPalette({ open, onClose, navGroups, onNavigate, currentView, onStationCard, onLogSearch }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Splaszcz nawigacje do listy [{id, label, group}]
  const allItems = [];
  for (const g of navGroups || []) {
    if (g.type === 'single') {
      allItems.push({ id: g.id, label: stripEmoji(g.label), group: '' });
    } else if (g.items) {
      for (const [id, label] of g.items) {
        allItems.push({ id, label: stripEmoji(label), group: stripEmoji(g.label) });
      }
    }
  }

  const call = query.trim().toUpperCase();
  const actions = CALL_RE.test(call) ? [
    ...(onStationCard ? [{ id: 'act-card', action: () => onStationCard(call), icon: 'user', label: tx('Karta stacji {0}', [call]), group: 'Znak' }] : []),
    ...(onLogSearch ? [{ id: 'act-log', action: () => onLogSearch(call), icon: 'book', label: tx('Szukaj {0} w dzienniku', [call]), group: 'Znak' }] : []),
  ] : [];
  const matches = [...actions, ...allItems.filter(it =>
    fuzzyMatch(query, it.label) || fuzzyMatch(query, it.group))];

  // Reset przy otwarciu + fokus na pole
  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // setTimeout: pole musi juz istniec w DOM
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // Kursor nie moze wyjsc poza liste po zmianie filtra
  useEffect(() => { setCursor(c => Math.min(c, Math.max(0, matches.length - 1))); },
    [query, matches.length]);

  const choose = useCallback((item) => {
    if (!item) return;
    if (item.action) item.action();
    else onNavigate(item.id);
    onClose();
  }, [onNavigate, onClose]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, matches.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(matches[cursor]); }
  };

  // Przewijaj do zaznaczonej pozycji
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${cursor}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  return (
    <div className="palette-overlay" onClick={onClose}>
      <div className="palette" role="dialog" aria-label={tx('Szukaj')} onClick={e => e.stopPropagation()}>
        <div className="palette__search">
          <Icon name="search" size={18} />
          <input ref={inputRef} value={query} onKeyDown={onKeyDown}
            onChange={e => { setQuery(e.target.value); setCursor(0); }}
            placeholder={tx('Szukaj widoku albo wpisz znak (np. „dxcc”, „SP3ABC”)')} aria-label={tx('Szukaj')} />
        </div>

        <div ref={listRef} className="palette__list">
          {matches.length === 0 && (
            <div className="palette__empty">{tx('Nic nie pasuje do „')}{query}”</div>
          )}
          {matches.map((it, i) => (
            <button key={it.id} type="button" data-idx={i}
              className={'palette__item' + (i === cursor ? ' palette__item--active' : '')}
              onClick={() => choose(it)}
              onMouseEnter={() => setCursor(i)}>
              <Icon name={it.icon || it.id} size={17} />
              <span className="palette__label">{it.label}</span>
              {it.group && <span className="palette__group">{it.group}</span>}
              {it.id === currentView && <span className="palette__here">{tx('tu jesteś')}</span>}
            </button>
          ))}
        </div>

        {/* Skroty klawiszowe -- wczesniej ISTNIALY, ale nikt o nich nie wiedzial */}
        <div className="palette__foot">
          <span><Kbd>↑</Kbd><Kbd>↓</Kbd> {tx('wybór')}</span>
          <span><Kbd>{tx('Enter')}</Kbd> {tx('przejdź')}</span>
          <span><Kbd>{tx('Esc')}</Kbd> {tx('zamknij')}</span>
          <span style={{ marginLeft: 'auto' }}>
            {tx('W loggerze:')} <Kbd>{tx('Ctrl')}</Kbd>+<Kbd>{tx('Enter')}</Kbd> {tx('zapis ·')} <Kbd>{tx('Esc')}</Kbd> {tx('czyść')}
          </span>
        </div>
      </div>
    </div>
  );
}

function Kbd({ children }) {
  return <kbd className="kbd">{children}</kbd>;
}
